import { normalizeCountryLabel } from "../../../../../../../../../../_shared/_logic/countries"

export type AddressDuplicateInput = {
  street?: string | null
  house_number?: string | null
  zip?: string | null
  city?: string | null
  country?: string | null
}

export const normalizeAddressInput = <T extends AddressDuplicateInput>(data: T): T =>
  ({
    ...data,
    street: (data.street ?? "").trim(),
    house_number: (data.house_number ?? "").trim(),
    zip: (data.zip ?? "").trim(),
    city: (data.city ?? "").trim(),
    country: normalizeCountryLabel(data.country),
  }) as T

const normalizeText = (value: string | null | undefined) =>
  (value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/gi, "ss")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()

const normalizeStreetName = (value: string | null | undefined) =>
  normalizeText(value)
    .replace(/\bstr\.\b/g, "strasse")
    .replace(/\bstr\b/g, "strasse")
    .replace(/straße/g, "strasse")
    .replace(/[^a-z0-9]/g, "")

const normalizeHouseNumber = (value: string | null | undefined) =>
  (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "")

const normalizeZip = (value: string | null | undefined) =>
  (value ?? "").replace(/\s+/g, "").toLowerCase()

const levenshteinDistance = (a: string, b: string) => {
  if (a === b) return 0

  const rows = a.length + 1
  const cols = b.length + 1
  const matrix: number[][] = Array.from({ length: rows }, (_, i) => {
    const row = new Array<number>(cols).fill(0)
    row[0] = i

    return row
  })

  for (let j = 0; j < cols; j += 1) {
    matrix[0][j] = j
  }

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      )
    }
  }

  return matrix[rows - 1][cols - 1]
}

const streetSimilarity = (a: string | null | undefined, b: string | null | undefined) => {
  const normA = normalizeStreetName(a)
  const normB = normalizeStreetName(b)

  if (!normA || !normB) return 0

  if (normA === normB) return 1

  const distance = levenshteinDistance(normA, normB)
  const longest = Math.max(normA.length, normB.length) || 1

  return 1 - distance / longest
}

export const isPotentialAddressDuplicate = (candidate: Property, target: AddressDuplicateInput) => {
  if (normalizeCountryLabel(candidate.country) !== normalizeCountryLabel(target.country))
    return false

  const zipMatch =
    normalizeZip(candidate.zip) === normalizeZip(target.zip) && Boolean(normalizeZip(target.zip))
  const houseMatch =
    normalizeHouseNumber(candidate.house_number) === normalizeHouseNumber(target.house_number) &&
    Boolean(normalizeHouseNumber(target.house_number))

  if (!zipMatch && !houseMatch) return false

  const streetScore = streetSimilarity(candidate.street, target.street)

  if (streetScore >= 0.82 && zipMatch && houseMatch) return true

  if (streetScore >= 0.9 && (zipMatch || houseMatch)) return true

  return false
}

export const hasAddressDuplicateSearchSeed = (data: AddressDuplicateInput) =>
  Boolean(
    (data.street ?? "").trim() ||
    (data.house_number ?? "").trim() ||
    (data.zip ?? "").trim() ||
    (data.city ?? "").trim(),
  )

export const findSimilarPropertiesInList = ({
  properties,
  address,
  excludePropertyId = null,
}: {
  properties: Property[]
  address: AddressDuplicateInput
  excludePropertyId?: string | null
}) => {
  const normalized = normalizeAddressInput(address)

  return properties
    .filter((property) => property && (!excludePropertyId || property.id !== excludePropertyId))
    .filter((property) => isPotentialAddressDuplicate(property, normalized))
}

export const formatSimilarAddress = (property: Property) =>
  [
    [property.street, property.house_number].filter(Boolean).join(" ").trim(),
    [property.zip, property.city].filter(Boolean).join(" ").trim(),
    normalizeCountryLabel(property.country),
  ]
    .filter(Boolean)
    .join(", ")
